import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Timeline as MuiTimeline} from '@mui/lab';
import TimelineItem from "./item";

/**
 * @typedef {{content: any, oppositeContent: any, id: string | number, data: any, toggleable: boolean}} TimelineData
 */

/**
 * @typedef {string | number |  Array<string | number | null> | null} TimelineSelection
 */

/**
 * @param {TimelineData[]} data
 * @param {TimelineSelection} selectionProps
 * @param {function(data: TimelineData | TimelineData[] | null): void} onSelectionChanged
 * @param {string} title
 * @param {boolean} preserveDataOrderInSelection
 * @param {boolean} multiple
 * @return {JSX.Element}
 * @constructor
 */
const Timeline = ({
                      data,
                      selection: selectionProps,
                      onSelectionChanged,
                      title = 'Time Line',
                      multiple = false,
                      preserveDataOrderInSelection = false,
                  }) => {
    const [selection, setSelection] = useState(multiple ? Array.isArray(selectionProps) ? selectionProps : [selectionProps] : selectionProps);
    const ignoreChange = useRef(true);

    /**
     * With each change in selectedId:
     * - syncs the selection state with the new value of selectedId.
     * - sets the ignoreChange to true so that this change does not trigger the selection callback
     */
    useLayoutEffect(() => {
        setSelection(multiple
            ? Array.isArray(selectionProps)
                ? selectionProps
                : typeof selectionProps !== 'undefined' && selection !== null
                    ? [selectionProps]
                    : []
            : selectionProps)
        ignoreChange.current = true;
    }, [selectionProps])

    /**
     * With each change in selection:
     * - calls the appropriate callback on the change of the selection with the new selection if this change should
     * not be ignored.
     */
    useEffect(() => {
        if (ignoreChange.current) {
            ignoreChange.current = false;
            return
        }
        if (onSelectionChanged) {
            if (multiple) {
                // if preserve the order of data in selection, then use data to filter, otherwise use selection to map
                if (preserveDataOrderInSelection) {
                    onSelectionChanged(data.filter(e => selection.includes(e.id)))
                } else {
                    onSelectionChanged(selection.map(e => data.find(d => d.id === e)))
                }
            } else {
                onSelectionChanged(data.find(e => typeof selection !== 'undefined' && selection !== null && e.id === selection));
            }
        }
    }, [selection])


    /**
     * Selects the item by calling the appropriate callback if it exists.
     *
     * * if item is not toggleable, then we can not change its selection state.
     * * if the given item is already selected, un-selects it.
     * * if this timeline is multiple, then removes or add the item if it exists or not respectively, and calls the
     * callback with the new list of selections.
     * @param {TimelineData} item
     */
    const toggleSelectedItem = (item) => {
        if (!item.toggleable)
            return;
        if (multiple) {
            return setSelection(prevState => {
                let newSelection
                if (selection.includes(item.id)) {
                    newSelection = prevState.filter(e => e !== item.id)
                } else {
                    newSelection = [...prevState, item.id]
                }
                return newSelection;
            })
        }
        if (item.id === selection) {
            setSelection(null);
        } else {
            setSelection(item.id);
        }
    }

    return (
        <>
            <MuiTimeline className={'timeline'} position="alternate">
                <TimelineItem ends item={{content: title ?? "Time Line"}}/>
                {
                    data?.map((item) => (
                        <TimelineItem
                            key={item.id}
                            item={item}
                            multiple={multiple}
                            selection={selection}
                            toggleSelection={toggleSelectedItem}
                        />
                    ))
                }
                <TimelineItem ends hideConnector/>
            </MuiTimeline>
        </>
    )
}

export default Timeline;
